import { KeenSliderPlugin } from 'keen-slider/react';

const linear = (t: number) => t;
const DURATION_PER_SLIDE_MS = 13_000;

export type AutoplayPluginOptions = {
  /** When provided, autoplay only runs while the media query matches; below it, it stays paused. */
  enabledMediaQuery?: string;
};

export const createAutoplayPlugin = ({ enabledMediaQuery }: AutoplayPluginOptions = {}): KeenSliderPlugin => (slider) => {
  let isPaused = false;
  let isEnabled = true;
  let isHovering = false;
  let isFocusWithin = false;
  /**
   * Whether the running animation was started by autoplay, as opposed to a drag's
   * free-mode momentum or another plugin moving the slider.
   */
  let isAutoplayAnimation = false;

  const goToNextSlide = () => {
    if (isPaused || !isEnabled) return;

    isAutoplayAnimation = true;
    slider.moveToIdx(slider.track.details.abs + 1, true, { duration: DURATION_PER_SLIDE_MS, easing: linear });
  };

  const handleAnimationEnded = () => {
    /* This runs after every animation, including a drag's free-mode momentum */
    const wasAutoplayAnimation = isAutoplayAnimation;

    isAutoplayAnimation = false;

    if (isPaused || !isEnabled) return;

    const { rel: relativePosition, progress: sliderProgressPercentage } = slider.track.details;
    /**
     * Only autoplay's own moves land exactly on a slide — after a drag the slider can stop
     * anywhere near slide 0, and resetting then would visibly snap it into place.
     */
    const isSliderLoopComplete = wasAutoplayAnimation && relativePosition === 0 && sliderProgressPercentage !== 0;

    if (isSliderLoopComplete) {
      /**
       * This prevents a bug where the slider would rewind to the last slide
       * when it reaches the end of the loop, instead of going back to the first slide.
       */
      isAutoplayAnimation = true;
      slider.moveToIdx(0, true, { duration: 0 });
    } else {
      goToNextSlide();
    }
  };

  const pauseAnimation = () => {
    isPaused = true;
    isAutoplayAnimation = false;
    slider.animator.stop();
  };

  const resumeAnimation = () => {
    isPaused = false;

    requestAnimationFrame(() => {
      if (!isPaused && !slider.animator.active) goToNextSlide();
    });
  };

  const handleMouseEnter = () => {
    isHovering = true;
    pauseAnimation();
  };

  const handleMouseLeave = () => {
    isHovering = false;
    if (!isFocusWithin) resumeAnimation();
  };

  /**
   * Slides are links, so pressing the mouse on one (e.g. to drag) focuses it. Only keyboard
   * focus should pause autoplay — otherwise it wouldn't resume when the cursor leaves.
   */
  const handleFocusIn = (event: FocusEvent) => {
    if (!(event.target instanceof Element) || !event.target.matches(':focus-visible')) return;

    isFocusWithin = true;
    pauseAnimation();
  };

  /**
   * focusout fires every time focus moves off any slide, including
   * when it's moving to the next slide within this same carousel — relatedTarget
   * tells us whether focus actually left the container or just moved within it.
   */
  const handleFocusOut = (event: FocusEvent) => {
    if (event.relatedTarget instanceof Node && slider.container.contains(event.relatedTarget)) return;

    isFocusWithin = false;
    if (!isHovering) resumeAnimation();
  };

  /**
   * A click, or a drag that ends with the cursor still resting on the slider, shouldn't
   * resume autoplay — only leaving the slider (or disabling on media change) should.
   */
  const handleDragEnded = () => {
    if (!isHovering && !isFocusWithin) resumeAnimation();
  };

  const updateAnimationStatus = (mediaQuery: MediaQueryList | MediaQueryListEvent) => {
    isEnabled = mediaQuery.matches;

    isEnabled ? resumeAnimation() : pauseAnimation();
  };

  /**
   * keen-slider stops the animator internally when the window resizes (to recalculate
   * slide sizes) and emits 'updated' afterwards, without restarting the animation itself.
   */
  const handleUpdated = () => {
    if (isEnabled && !isHovering && !isFocusWithin) resumeAnimation();
  };

  slider.on('created', () => {
    slider.container.addEventListener('mouseenter', handleMouseEnter);
    slider.container.addEventListener('mouseleave', handleMouseLeave);
    slider.container.addEventListener('focusin', handleFocusIn);
    slider.container.addEventListener('focusout', handleFocusOut);

    if (enabledMediaQuery) {
      const mediaQuery = window.matchMedia(enabledMediaQuery);

      isEnabled = mediaQuery.matches;
      mediaQuery.addEventListener('change', updateAnimationStatus);
    }

    if (isEnabled) goToNextSlide();
  });

  slider.on('animationEnded', handleAnimationEnded);
  slider.on('dragStarted', pauseAnimation);
  slider.on('dragEnded', handleDragEnded);
  slider.on('updated', handleUpdated);
};
